import { GridButtons } from "@/controls/buttons/GridButtons";
import { upsertSection } from "@/pages/configuration/repositories/SectionRepository";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { useState } from "react";
import { Section } from "../../../models/Section";
import { useUserContext } from "@dexteel/mesf-core";

type Props = {
	setSelectedRowData: (section: Section) => void;
	setShowModal: (modal: "create" | "update" | "delete" | "") => void;
	showContextMenu: Function;
	setError: (error: string) => void;
};
export const useTableData = ({
	setSelectedRowData,
	setShowModal,
	showContextMenu,
	setError,
}: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const {
		state: { defaultAreaId },
	} = useUserContext();
	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertSection, {
		onSuccess: async (id: number) => {
			await queryClient.invalidateQueries(["sections"]);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const columnDefs: ColDef[] = [
		{
			field: "MoveUp",
			headerName: "",
			maxWidth: 30,
			flex: 1,
			cellRenderer: (params: ICellRendererParams) => (
				<IconButton
					size="small"
					onClick={() => {
						upsertMutation.mutate({
							section: {
								...params.data,
								SortOrder: params.data.SortOrder + 1,
							},
							defaultAreaId,
						});
					}}
					title="Move down"
					color="primary"
					disabled={(params.data as Section).IsLastSection || isSubmitLoading}
				>
					<KeyboardArrowDown />
				</IconButton>
			),
		},
		{
			field: "MoveDown",
			headerName: "",
			maxWidth: 30,
			flex: 1,
			cellRenderer: (params: ICellRendererParams) => (
				<IconButton
					size="small"
					onClick={() => {
						upsertMutation.mutate({
							section: {
								...params.data,
								SortOrder: params.data.SortOrder - 1,
							},
							defaultAreaId,
						});
					}}
					title="Move up"
					color="primary"
					disabled={(params.data as Section).IsFirstSection || isSubmitLoading}
				>
					<KeyboardArrowUp />
				</IconButton>
			),
		},
		{
			field: "SectionName",
			headerName: "Name",
			minWidth: 20,
			flex: 1,
		},
		{
			field: "SectionFamilyName",
			headerName: "Family",
			minWidth: 20,
			flex: 1,
		},
		{
			cellRenderer: GridButtons,
			cellRendererParams: (params: ICellRendererParams) => ({
				setId: setSelectedRowData,
				id: params.data as Section,
				setShowModal: setShowModal,
				showContextMenu: () =>
					showContextMenu(event, params.data, "sectionContext"),
			}),
			wrapText: false,
			autoHeight: false,
			wrapHeaderText: false,
			headerName: "",
			sortable: false,
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
			filter: false,
			autoHeaderHeight: true,
		},
	];
	return { columnDefs };
};
